import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Spinner } from "@/components/UIKit";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, ArrowRight, ArrowLeft, ShieldCheck, Building2, Zap,
  Mail, TrendingUp, FileCheck, Calculator, MessageSquare, Rocket, Loader2,
  KeyRound, ExternalLink, Sparkles, SkipForward,
} from "lucide-react";

/**
 * Onboarding-veiviser for førstegangs oppsett av en kraftleverandør.
 *
 * Rekkefølge: Velkommen → Selskap → 2FA → Resend → Nord Pool → Elhub
 * → PEPPOL → Regnskap → Twilio → Ferdig.
 */
export default function Onboarding() {
  const nav = useNavigate();
  const { user, tenant } = useAuth();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/onboarding/status");
    setStatus(data);
    // Hopp til første ufullførte steg automatisk første gang
    if (step === 0 && !data.completed && !data.skipped) {
      const firstIncomplete = pickFirstIncomplete(data.checklist);
      if (firstIncomplete !== null) setStep(firstIncomplete);
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const STEPS = useMemo(() => [
    { key: "welcome", title: "Velkommen", icon: Sparkles, required: false },
    { key: "tenant", title: "Selskapsinfo", icon: Building2, required: true },
    { key: "twofa", title: "2FA", icon: ShieldCheck, required: true },
    { key: "resend", title: "Resend", icon: Mail, required: false },
    { key: "nord_pool", title: "Nord Pool", icon: TrendingUp, required: false },
    { key: "elhub", title: "Elhub", icon: Zap, required: false },
    { key: "peppol_ehf", title: "PEPPOL", icon: FileCheck, required: false },
    { key: "accounting", title: "Regnskap", icon: Calculator, required: false },
    { key: "twilio", title: "Twilio", icon: MessageSquare, required: false },
    { key: "done", title: "Ferdig", icon: Rocket, required: false },
  ], []);

  function pickFirstIncomplete(checklist) {
    const map = ["welcome", "tenant", "twofa", "resend", "nord_pool", "elhub", "peppol_ehf", "accounting", "twilio", "done"];
    for (let i = 1; i < map.length - 1; i++) {
      if (!checklist[map[i]]) return i;
    }
    return map.length - 1;
  }

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/complete");
      toast.success("Onboarding fullført");
      nav("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke fullføre"); }
    finally { setSaving(false); }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/skip");
      toast.success("Onboarding hoppet over — du kan komme tilbake når som helst");
      nav("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setSaving(false); }
  };

  if (!status) return <div className="p-8"><Spinner label="Henter status…" /></div>;

  const current = STEPS[step];
  const isChecked = (key) => status.checklist[key];

  return (
    <div data-testid="onboarding-page" className="max-w-6xl">
      <PageHeader
        title="Kom i gang med Krafthub"
        subtitle={`Steg-for-steg oppsett for ${tenant?.name || "din tenant"}. Fremdrift: ${status.progress_percent}%`}
        actions={
          <button onClick={skip} disabled={saving}
            className="h-9 px-3 rounded-md border border-zinc-800 text-zinc-400 text-xs hover:text-zinc-200 flex items-center gap-1.5"
            data-testid="onboarding-skip">
            <SkipForward className="h-3.5 w-3.5" /> Hopp over
          </button>
        }
      />

      {/* Progress bar */}
      <div className="mb-6">
        <div className="h-1.5 w-full bg-zinc-900 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
            style={{ width: `${status.progress_percent}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Steg-liste */}
        <Card className="p-3 h-max sticky top-2" testid="onboarding-sidebar">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = s.key === "welcome" || s.key === "done" ? false : isChecked(s.key);
            const active = i === step;
            return (
              <button
                key={s.key}
                onClick={() => setStep(i)}
                data-testid={`onboarding-nav-${s.key}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-sm mb-0.5 transition-colors ${
                  active ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 border border-transparent"
                }`}
              >
                <div className="shrink-0">
                  {done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        : <Circle className={`h-4 w-4 ${active ? "text-emerald-400" : "text-zinc-600"}`} />}
                </div>
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{s.title}</span>
                {s.required && !done && (
                  <span className="ml-auto text-[9px] uppercase tracking-wider text-amber-400/80">Påkrevd</span>
                )}
              </button>
            );
          })}
        </Card>

        {/* Steg-innhold */}
        <div>
          {current.key === "welcome" && <StepWelcome tenant={tenant} user={user} onNext={() => setStep(step + 1)} />}
          {current.key === "tenant" && <StepTenant tenant={tenant} onDone={load} onNext={() => setStep(step + 1)} onBack={() => setStep(step - 1)} isChecked={isChecked("tenant")} />}
          {current.key === "twofa" && <StepTwoFA user={user} onDone={load} onNext={() => setStep(step + 1)} onBack={() => setStep(step - 1)} isChecked={isChecked("twofa")} />}
          {current.key === "resend" && <StepIntegration kind="resend" title="Resend (e-post)" icon={Mail}
            help="Sender fakturaer, purringer og portal-invitasjoner via e-post."
            getUrl="https://resend.com" howto={[
              "Registrer deg på resend.com",
              "Verifiser eget domene via DNS-TXT-post",
              "Gå til API Keys → «Create API Key»",
              "Kopier nøkkelen (starter med re_...)"
            ]} onDone={load} onNext={() => setStep(step + 1)} onBack={() => setStep(step - 1)} isChecked={isChecked("resend")} />}
          {current.key === "nord_pool" && <StepIntegration kind="nord_pool" title="Nord Pool (spotpris)" icon={TrendingUp}
            help="Henter offisielle spotpriser hver time for NO1–NO5."
            getUrl="https://www.nordpoolgroup.com" howto={[
              "Kontakt Nord Pool AS via nordpoolgroup.com → «Data Services»",
              "Bestill API-tilgang (som RME-registrert leverandør er dette ofte inkludert)",
              "Motta API-nøkkel + endpoint URL"
            ]} onDone={load} onNext={() => setStep(step + 1)} onBack={() => setStep(step - 1)} isChecked={isChecked("nord_pool")} />}
          {current.key === "elhub" && <StepElhub onDone={load} onNext={() => setStep(step + 1)} onBack={() => setStep(step - 1)} isChecked={isChecked("elhub")} />}
          {current.key === "peppol_ehf" && <StepIntegration kind="peppol_ehf" title="PEPPOL / EHF" icon={FileCheck}
            help="Elektronisk faktura til bedrifter og offentlig sektor (påkrevd for offentlig)."
            getUrl="https://www.sendregning.no" howto={[
              "Registrer PEPPOL-adresse via Sendregning, Unimicro eller annen Access Point",
              "Motta endpoint-URL, avsender-ID (9908:orgnr) og PEM-sertifikat",
              "Sett inn feltene her under"
            ]} onDone={load} onNext={() => setStep(step + 1)} onBack={() => setStep(step - 1)} isChecked={isChecked("peppol_ehf")} />}
          {current.key === "accounting" && <StepAccounting onDone={load} onNext={() => setStep(step + 1)} onBack={() => setStep(step - 1)} isChecked={isChecked("accounting")} />}
          {current.key === "twilio" && <StepIntegration kind="twilio" title="Twilio SMS" icon={MessageSquare}
            help="SMS-varsler for purringer og 2FA-koder. Valgfritt."
            getUrl="https://www.twilio.com" howto={[
              "Registrer deg på twilio.com",
              "Kopier Account SID + Auth Token fra Console",
              "Kjøp et norsk sender-nummer (+47…)"
            ]} onDone={load} onNext={() => setStep(step + 1)} onBack={() => setStep(step - 1)} isChecked={isChecked("twilio")} />}
          {current.key === "done" && <StepDone status={status} onFinish={finish} onBack={() => setStep(step - 1)} saving={saving} />}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STEG-KOMPONENTER
// ============================================================
function StepWelcome({ tenant, user, onNext }) {
  return (
    <Card className="p-8" testid="step-welcome">
      <div className="flex items-start gap-4 mb-6">
        <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 grid place-items-center shrink-0">
          <Sparkles className="h-6 w-6 text-emerald-400" />
        </div>
        <div>
          <h2 className="font-heading text-2xl font-semibold text-zinc-50">Velkommen, {user?.full_name?.split(" ")[0] || "admin"}!</h2>
          <p className="text-sm text-zinc-400 mt-1">La oss sette opp <span className="text-emerald-400">{tenant?.name}</span> i Krafthub.</p>
        </div>
      </div>

      <div className="text-sm text-zinc-300 leading-relaxed mb-5">
        Denne veiviseren tar deg gjennom alt som må konfigureres før dere er klare til å ta imot ekte kunder.
        Du kan hoppe over valgfrie steg og komme tilbake senere.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <InfoBlock title="Påkrevd" color="amber" items={[
          "Selskapsinformasjon (Brreg-oppslag)",
          "2FA på admin-kontoen din",
        ]} />
        <InfoBlock title="Anbefalt for produksjon" color="emerald" items={[
          "Resend for e-post",
          "Nord Pool for spotpriser",
          "Elhub for målepunkter",
        ]} />
        <InfoBlock title="Valgfritt" color="zinc" items={[
          "PEPPOL/EHF for bedriftsfakturaer",
          "Tripletex eller Fiken for regnskap",
          "Twilio for SMS-varsler",
        ]} />
        <InfoBlock title="Estimert tid" color="zinc" items={[
          "Uten API-nøkler: 5 min",
          "Med alle nøkler klare: 20 min",
          "Du kan alltid komme tilbake",
        ]} />
      </div>

      <div className="flex items-center gap-3">
        <button onClick={onNext} data-testid="welcome-next"
          className="h-11 px-6 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium flex items-center gap-2">
          Kom i gang <ArrowRight className="h-4 w-4" />
        </button>
        <a href="/status" className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
          Se produksjonsstatus <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </Card>
  );
}

function InfoBlock({ title, color, items }) {
  const cmap = {
    amber: "border-amber-500/20 bg-amber-500/5 text-amber-400",
    emerald: "border-emerald-500/20 bg-emerald-500/5 text-emerald-400",
    zinc: "border-zinc-800 bg-zinc-950/40 text-zinc-400",
  };
  return (
    <div className={`border rounded-md p-3.5 ${cmap[color]}`}>
      <div className="text-[11px] uppercase tracking-wider font-medium mb-2">{title}</div>
      <ul className="space-y-1 text-sm text-zinc-300">
        {items.map((i) => <li key={i} className="flex items-start gap-1.5"><span className="text-zinc-600 mt-1.5">•</span> {i}</li>)}
      </ul>
    </div>
  );
}

function StepTenant({ tenant, onDone, onNext, onBack, isChecked }) {
  const [form, setForm] = useState(tenant || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(tenant || {}); }, [tenant]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/tenant", form);
      toast.success("Selskapsinformasjon lagret");
      await onDone();
      onNext();
    } catch (e) { toast.error("Kunne ikke lagre"); }
    finally { setSaving(false); }
  };

  const field = (name, label, extra = {}) => (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</label>
      <input value={form[name] ?? ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm focus:outline-none focus:border-emerald-500/40"
        {...extra} data-testid={`onboarding-tenant-${name}`} />
    </div>
  );

  return (
    <StepFrame title="Selskapsinformasjon" subtitle="Verifiser kontaktinformasjon og standardverdier" done={isChecked}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        {field("name", "Selskapsnavn")}
        {field("orgnr", "Organisasjonsnummer", { disabled: true })}
        {field("contact_email", "Kontakt e-post", { type: "email", placeholder: "post@rstrom.no" })}
        {field("contact_phone", "Telefon", { placeholder: "+47 …" })}
        {field("address", "Besøksadresse")}
        <div className="grid grid-cols-2 gap-3">
          {field("postal_code", "Postnr")}
          {field("city", "Sted")}
        </div>
      </div>
      <StepFooter onBack={onBack} onNext={save} saving={saving} nextLabel="Lagre og fortsett" />
    </StepFrame>
  );
}

function StepTwoFA({ user, onDone, onNext, onBack, isChecked }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch { toast.error("Kunne ikke starte 2FA-oppsett"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA aktivert");
      setSetup(null);
      await onDone();
      onNext();
    } catch { toast.error("Ugyldig kode"); }
    finally { setBusy(false); }
  };

  return (
    <StepFrame title="Aktiver 2FA" subtitle="Tofaktor-autentisering (TOTP) for admin-kontoen din" done={isChecked}>
      <div className="text-sm text-zinc-300 leading-relaxed mb-5">
        Bruk Google Authenticator, Authy, 1Password eller lignende app. Dette er sterkt anbefalt for admin-kontoer siden Krafthub håndterer kundedata, fakturaer og betalinger.
      </div>

      {isChecked ? (
        <div className="border border-emerald-500/20 bg-emerald-500/5 rounded-md p-4 flex items-start gap-3 mb-5">
          <ShieldCheck className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-emerald-400 font-medium text-sm">2FA er allerede aktivert på kontoen din</div>
            <div className="text-xs text-zinc-500 mt-0.5">Du kan deaktivere det under Innstillinger → Sikkerhet hvis du må.</div>
          </div>
        </div>
      ) : setup ? (
        <div className="border border-zinc-800 rounded-md p-5 mb-5 bg-zinc-950/40">
          <div className="text-xs text-zinc-500 mb-2">Skann QR eller skriv inn manuelt i autentiserings-appen:</div>
          <div className="font-mono text-xs bg-zinc-900 p-3 rounded border border-zinc-800 mb-4 break-all select-all" data-testid="twofa-secret">
            {setup.secret}
          </div>
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            maxLength={6} placeholder="6-sifret engangskode"
            data-testid="twofa-code"
            className="w-full h-11 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-center tracking-widest text-lg mb-2" />
          <button onClick={verify} disabled={busy || code.length !== 6}
            data-testid="twofa-verify"
            className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Bekreft og aktiver
          </button>
        </div>
      ) : (
        <button onClick={start} disabled={busy}
          data-testid="twofa-start"
          className="h-11 px-5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium flex items-center gap-2 mb-5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Sett opp 2FA nå
        </button>
      )}

      <StepFooter onBack={onBack} onNext={onNext} nextLabel={isChecked ? "Fortsett" : "Hopp over foreløpig"} secondary={!isChecked} />
    </StepFrame>
  );
}

function StepElhub({ onDone, onNext, onBack, isChecked }) {
  const [form, setForm] = useState({
    api_url: "https://api.elhub.no/energy-data/v1",
    client_id: "", client_secret: "", party_id: "", cert_password: "",
  });
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [certInfo, setCertInfo] = useState(null);

  useEffect(() => { (async () => {
    const { data } = await api.get("/integrations");
    const cur = data.elhub || {};
    setEnabled(!!cur.enabled);
    setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(cur.config || {}).filter(([k, v]) => v && !String(v).includes("•"))) }));
  })(); }, []);

  const uploadCert = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/elhub/certificate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCertInfo(data);
      toast.success(`Sertifikat lastet opp (${(data.size_bytes / 1024).toFixed(1)} kB)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/integrations/elhub", { enabled: true, sandbox: true, config: form });
      toast.success("Elhub konfigurert i sandbox");
      await onDone();
      onNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setSaving(false); }
  };

  return (
    <StepFrame title="Elhub" subtitle="Norsk sentral for målepunktdata (BRS-meldinger)" done={isChecked}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <FormField label="API URL" value={form.api_url} onChange={(v) => setForm({ ...form, api_url: v })} testid="elhub-api-url" />
        <FormField label="Party ID (GLN)" value={form.party_id} onChange={(v) => setForm({ ...form, party_id: v })} placeholder="7080005051286" testid="elhub-party-id" />
        <FormField label="Client ID" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} testid="elhub-client-id" />
        <FormField label="Client Secret" type="password" value={form.client_secret} onChange={(v) => setForm({ ...form, client_secret: v })} testid="elhub-client-secret" />
        <FormField label="Sertifikat-passord" type="password" value={form.cert_password} onChange={(v) => setForm({ ...form, cert_password: v })} testid="elhub-cert-password" />
      </div>

      <div className="border border-zinc-900 rounded-md p-4 mb-4 bg-zinc-950/40">
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Last opp PKCS12/PEM-sertifikat</label>
        <label className="inline-flex h-10 px-4 rounded-md border border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-400 text-sm items-center gap-2 cursor-pointer">
          <input type="file" accept=".p12,.pfx,.pem,.crt,.cer" className="hidden"
            data-testid="onboarding-elhub-cert"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCert(f); e.target.value = ""; }} />
          Velg sertifikatfil
        </label>
        {certInfo && (
          <div className="text-xs text-emerald-400 mt-2">
            <CheckCircle2 className="h-3 w-3 inline mr-1" /> {certInfo.filename} · {(certInfo.size_bytes/1024).toFixed(1)} kB
          </div>
        )}
        <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          Sertifikatet krypteres med Fernet før lagring. Kan lastes opp senere via Innstillinger → Integrasjoner → Elhub.
        </div>
      </div>

      <HelpBox items={[
        "Søk om Elhub-tilgang via Statnett (krever gyldig RME-konsesjon)",
        "Motta Client ID + Secret på e-post",
        "Bestill PKCS12-sertifikat via BankID eller Buypass",
        "Party ID (GLN) er 13-sifret — finnes i RME/Statnett-konsesjonsdokumentet",
      ]} link="https://elhub.no/leverandor" />

      <StepFooter onBack={onBack} onNext={save} saving={saving} nextLabel={isChecked ? "Oppdater og fortsett" : "Lagre og fortsett"} />
    </StepFrame>
  );
}

function StepAccounting({ onDone, onNext, onBack, isChecked }) {
  const [choice, setChoice] = useState("tripletex");
  const [tri, setTri] = useState({ consumer_token: "", employee_token: "", company_id: "" });
  const [fiken, setFiken] = useState({ api_token: "", company_slug: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (choice === "tripletex") {
        await api.put("/integrations/tripletex", { enabled: true, sandbox: true, config: tri });
        toast.success("Tripletex konfigurert");
      } else {
        await api.put("/integrations/fiken", { enabled: true, sandbox: true, config: fiken });
        toast.success("Fiken konfigurert");
      }
      await onDone();
      onNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setSaving(false); }
  };

  return (
    <StepFrame title="Regnskapssystem" subtitle="Velg Tripletex eller Fiken for automatisk regnskapsføring (valgfritt)" done={isChecked}>
      <div className="flex gap-2 mb-5">
        {["tripletex", "fiken"].map((k) => (
          <button key={k} onClick={() => setChoice(k)}
            data-testid={`accounting-choose-${k}`}
            className={`h-10 px-5 rounded-md border text-sm font-medium capitalize ${
              choice === k ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-400" : "border-zinc-800 text-zinc-400 hover:text-zinc-100"
            }`}>
            {k}
          </button>
        ))}
      </div>

      {choice === "tripletex" ? (
        <div className="space-y-3 mb-4">
          <FormField label="Consumer Token" type="password" value={tri.consumer_token} onChange={(v) => setTri({ ...tri, consumer_token: v })}
            help="Krafthub sin app-nøkkel — spør support hvis du ikke har den" testid="tri-consumer" />
          <FormField label="Employee Token" type="password" value={tri.employee_token} onChange={(v) => setTri({ ...tri, employee_token: v })}
            help="Din personlige nøkkel. Genereres i Tripletex → Selskap → Vår kontoinformasjon → API-tilgang" testid="tri-employee" />
          <FormField label="Selskaps-ID" value={tri.company_id} onChange={(v) => setTri({ ...tri, company_id: v })}
            help="Heltall du finner i URL: tripletex.no/execute/company/?id=[SELSKAPS-ID]" testid="tri-company" />
        </div>
      ) : (
        <div className="space-y-3 mb-4">
          <FormField label="Fiken API-nøkkel" type="password" value={fiken.api_token} onChange={(v) => setFiken({ ...fiken, api_token: v })}
            help="Innstillinger → API i Fiken" testid="fiken-token" />
          <FormField label="Selskaps-slug" value={fiken.company_slug} onChange={(v) => setFiken({ ...fiken, company_slug: v })}
            help="Fra URL: fiken.no/foretak/[SLUG]" testid="fiken-slug" />
        </div>
      )}

      <StepFooter onBack={onBack} onNext={save} saving={saving} nextLabel="Lagre og fortsett" secondaryLabel="Hopp over" secondary onSecondary={onNext} />
    </StepFrame>
  );
}

function StepIntegration({ kind, title, icon: Icon, help, howto, getUrl, onDone, onNext, onBack, isChecked }) {
  const [schema, setSchema] = useState(null);
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    const [{ data: schemas }, { data: state }] = await Promise.all([
      api.get("/integrations/schemas"),
      api.get("/integrations"),
    ]);
    setSchema(schemas[kind]);
    const cur = state[kind]?.config || {};
    // Ta ut maskerte verdier
    const clean = Object.fromEntries(Object.entries(cur).filter(([, v]) => v && !String(v).includes("•")));
    setConfig(clean);
  })(); }, [kind]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/integrations/${kind}`, { enabled: true, sandbox: true, config });
      toast.success(`${title} konfigurert`);
      await onDone();
      onNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setSaving(false); }
  };

  if (!schema) return <Card className="p-8"><Spinner /></Card>;

  return (
    <StepFrame title={title} subtitle={help} done={isChecked} icon={Icon}>
      <div className="space-y-3 mb-4" data-testid={`onboarding-integ-${kind}`}>
        {schema.fields.filter((f) => f.type !== "textarea" || kind !== "elhub").map((f) => (
          <FormField key={f.name}
            label={f.label}
            type={f.type === "password" ? "password" : f.type === "textarea" ? "textarea" : "text"}
            placeholder={f.placeholder}
            value={config[f.name] || ""}
            onChange={(v) => setConfig({ ...config, [f.name]: v })}
            testid={`onboarding-${kind}-${f.name}`}
          />
        ))}
      </div>

      <HelpBox items={howto} link={getUrl} />

      <StepFooter onBack={onBack} onNext={save} saving={saving} nextLabel="Lagre og fortsett" secondaryLabel="Hopp over" secondary onSecondary={onNext} />
    </StepFrame>
  );
}

function StepDone({ status, onFinish, onBack, saving }) {
  const items = [
    { key: "tenant", label: "Selskapsinformasjon" },
    { key: "twofa", label: "2FA aktivert" },
    { key: "resend", label: "Resend (e-post)" },
    { key: "nord_pool", label: "Nord Pool (spotpris)" },
    { key: "elhub", label: "Elhub" },
    { key: "peppol_ehf", label: "PEPPOL / EHF" },
    { key: "accounting", label: "Regnskap (Tripletex/Fiken)" },
    { key: "twilio", label: "Twilio SMS" },
  ];
  return (
    <Card className="p-8" testid="step-done">
      <div className="flex items-start gap-4 mb-6">
        <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 grid place-items-center shrink-0">
          <Rocket className="h-6 w-6 text-emerald-400" />
        </div>
        <div>
          <h2 className="font-heading text-2xl font-semibold text-zinc-50">Klar til å gå live!</h2>
          <p className="text-sm text-zinc-400 mt-1">Din onboarding-status nedenfor. Du kan alltid gå tilbake og fylle ut mer senere.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
        {items.map((i) => {
          const done = status.checklist[i.key];
          return (
            <div key={i.key} className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
              done ? "border-emerald-500/20 bg-emerald-500/5 text-zinc-100" : "border-zinc-900 bg-zinc-950/40 text-zinc-500"
            }`}>
              {done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-zinc-700" />}
              <span>{i.label}</span>
              {done ? <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-400">OK</span> :
                      <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-600">Ikke satt</span>}
            </div>
          );
        })}
      </div>

      {!status.all_required_ok && (
        <div className="border border-amber-500/20 bg-amber-500/5 rounded-md p-3 mb-5 text-xs text-amber-400">
          Du mangler noen påkrevde steg (selskapsinfo og 2FA). Du kan fortsatt fullføre, men systemet vil vise påminnelser til de er satt.
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="h-10 px-4 rounded-md border border-zinc-800 text-zinc-300 text-sm flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Tilbake
        </button>
        <button onClick={onFinish} disabled={saving}
          data-testid="onboarding-finish"
          className="h-11 px-6 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Fullfør onboarding <Rocket className="h-4 w-4" />
        </button>
      </div>
    </Card>
  );
}

// ============================================================
// FELLES BYGGKLOSSER
// ============================================================
function StepFrame({ title, subtitle, done, children, icon: Icon }) {
  return (
    <Card className="p-6" testid={`step-frame-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start gap-3 mb-5 pb-4 border-b border-zinc-900">
        {Icon && (
          <div className="h-10 w-10 rounded-md bg-zinc-900 border border-zinc-800 grid place-items-center shrink-0">
            <Icon className="h-5 w-5 text-emerald-400" />
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-xl font-semibold text-zinc-50">{title}</h3>
            {done && <span className="text-[10px] uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded">Fullført</span>}
          </div>
          {subtitle && <p className="text-sm text-zinc-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </Card>
  );
}

function StepFooter({ onBack, onNext, saving, nextLabel = "Neste", secondary, secondaryLabel, onSecondary }) {
  return (
    <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-zinc-900">
      <button onClick={onBack}
        data-testid="step-back"
        className="h-10 px-4 rounded-md border border-zinc-800 text-zinc-300 text-sm flex items-center gap-1.5 hover:border-zinc-700">
        <ArrowLeft className="h-4 w-4" /> Tilbake
      </button>
      <div className="flex items-center gap-2">
        {secondary && (
          <button onClick={onSecondary}
            data-testid="step-secondary"
            className="h-10 px-4 rounded-md text-sm text-zinc-400 hover:text-zinc-100">
            {secondaryLabel || "Hopp over"}
          </button>
        )}
        <button onClick={onNext} disabled={saving}
          data-testid="step-next"
          className="h-10 px-5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium disabled:opacity-60 flex items-center gap-1.5">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} {nextLabel} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function FormField({ label, type = "text", value, onChange, placeholder, help, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</label>
      {type === "textarea" ? (
        <textarea rows={4} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          data-testid={testid}
          className="w-full px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm font-mono focus:outline-none focus:border-emerald-500/40" />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          data-testid={testid}
          className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm focus:outline-none focus:border-emerald-500/40" />
      )}
      {help && <div className="text-[11px] text-zinc-500 mt-1">{help}</div>}
    </div>
  );
}

function HelpBox({ items, link }) {
  return (
    <div className="border border-zinc-900 rounded-md p-3.5 bg-zinc-950/40 mb-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium mb-2">Slik får du tak i det</div>
      <ol className="text-xs text-zinc-300 space-y-1 list-decimal list-inside mb-2">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ol>
      {link && (
        <a href={link} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
          Åpne portal <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}
